import React from 'react';
import { FileSystemNavigator } from '../utils/filesystem';
import { makeProgramIcon } from './AppIcon';

type RecycleBinIconProps = {
  size?: number | string;
  className?: string;
};

const RECYCLE_BIN_PATH = 'C:\\$Recycle.Bin';
const EmptyRecycleBinIcon = makeProgramIcon('recycle-bin', 'recycle-bin');
const FullRecycleBinIcon = makeProgramIcon('recycle-bin-full', 'recycle-bin');

export const RecycleBinIcon: React.FC<RecycleBinIconProps> = ({
  size = 18,
  className,
}) => {
  const navigatorRef = React.useRef<FileSystemNavigator | null>(null);
  const [hasItems, setHasItems] = React.useState(false);

  if (!navigatorRef.current) {
    navigatorRef.current = new FileSystemNavigator();
  }

  React.useEffect(() => {
    let isMounted = true;

    const refreshIconState = async () => {
      const navigator = navigatorRef.current;
      if (!navigator) return;

      await navigator.waitForInitialization();
      await navigator.syncWithVFS();

      if (!isMounted) return;

      const recycleBinItems = navigator.getItemsInPath(RECYCLE_BIN_PATH);
      setHasItems(recycleBinItems.length > 0);
    };

    const handleRecycleBinChanged = () => {
      void refreshIconState();
    };

    const handleFileSystemChanged = (event: Event) => {
      const { detail } = event as CustomEvent<{ path?: string }>;
      const changedPath = detail?.path;

      if (
        changedPath === RECYCLE_BIN_PATH ||
        changedPath?.startsWith(`${RECYCLE_BIN_PATH}\\`)
      ) {
        void refreshIconState();
      }
    };

    void refreshIconState();
    window.addEventListener('recycleBinChanged', handleRecycleBinChanged);
    window.addEventListener('fileSystemChanged', handleFileSystemChanged as EventListener);

    return () => {
      isMounted = false;
      window.removeEventListener('recycleBinChanged', handleRecycleBinChanged);
      window.removeEventListener('fileSystemChanged', handleFileSystemChanged as EventListener);
    };
  }, []);

  const IconComponent = hasItems ? FullRecycleBinIcon : EmptyRecycleBinIcon;

  return <IconComponent size={size} className={className} />;
};
